import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { JobDetail } from '../../shared/api';
import { Banner, Button } from '../../components/ui';
import { ApiError, api } from '../../lib/api';

/**
 * Delete or archive. A job can be deleted until anything consequential exists
 * on it (a prepared notice, a document, a payment, a filing — the backend's
 * list); after that it can only be archived, which keeps every record but takes
 * it off the job list and out of the pilot's job count.
 */
export default function JobActions({ job, reload }: { job: JobDetail; reload: () => void }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<unknown>, after: () => void) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      after();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const archive = (archived: boolean) =>
    run(() => api.post(`/api/jobs/${job.id}/archive`, { archived }), archived ? () => navigate('/jobs') : reload);

  return (
    <div className="rounded-lg border border-border px-4 py-3">
      <p className="text-sm font-semibold text-ink">Remove this job</p>
      {job.archivedAt ? (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <p className="text-xs text-muted">Archived. Its records are kept; it is off your job list and the pilot count.</p>
          <Button variant="secondary" disabled={busy} onClick={() => void archive(false)}>
            Restore job
          </Button>
        </div>
      ) : job.deletable ? (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <p className="text-xs text-muted">Nothing has been prepared or recorded on this job yet, so it can be deleted.</p>
          {confirming ? (
            <>
              <Button
                disabled={busy}
                onClick={() => void run(() => api.del(`/api/jobs/${job.id}`), () => navigate('/jobs'))}
              >
                Delete permanently
              </Button>
              <Button variant="secondary" disabled={busy} onClick={() => setConfirming(false)}>
                Keep it
              </Button>
            </>
          ) : (
            <Button variant="secondary" disabled={busy} onClick={() => setConfirming(true)}>
              Delete job
            </Button>
          )}
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <p className="text-xs text-muted">
            Can’t be deleted because {job.deleteBlockers.join(', ')}. Archiving keeps every record.
          </p>
          <Button variant="secondary" disabled={busy} onClick={() => void archive(true)}>
            Archive job
          </Button>
        </div>
      )}
      {error && (
        <div className="mt-2">
          <Banner tone="urgent">{error}</Banner>
        </div>
      )}
    </div>
  );
}
